// @ts-check

/**
 * @file Renders a paginated async invoice-group select.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"

import {InvoiceGroup} from "../../frontend-models/invoice-group"
import ModelSelect from "../model-select"
import {MODEL_SELECT_PAGE_SIZE, modelSelectOptionsResult, normalizedSelectValues} from "../model-select-options"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/**
 * @typedef {object} InvoiceGroupsSelectProps
 * @property {string | number | null} [defaultValue] Initial selected invoice-group id.
 * @property {(string | number)[]} [defaultValues] Initial selected invoice-group ids.
 * @property {string} [label] Select label.
 * @property {boolean} [multiple] Allow multiple selections.
 * @property {string} [name] Api Maker form field name.
 * @property {(...args: unknown[]) => unknown} [onChangeValue] Selection change handler.
 * @property {string} [placeholder] Placeholder text.
 * @property {string} testID Stable test id.
 * @property {(string | number)[]} [values] Controlled selected values.
 */

/** @typedef {Record<string, never>} InvoiceGroupsSelectState */

/**
 * Invoice-group select that only loads the current page of labels.
 * @augments {ShapeComponent<InvoiceGroupsSelectProps, InvoiceGroupsSelectState>}
 */
class InvoiceGroupsSelect extends ShapeComponent {
  static propTypes = propTypesExact({
    defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    defaultValues: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    ),
    label: PropTypes.string,
    multiple: PropTypes.bool,
    name: PropTypes.string,
    onChangeValue: PropTypes.func,
    placeholder: PropTypes.string,
    testID: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    )
  })
  static defaultProps = {
    defaultValue: undefined,
    defaultValues: undefined,
    label: undefined,
    multiple: false,
    name: undefined,
    onChangeValue: undefined,
    placeholder: undefined,
    values: undefined
  }

  /** Initializes locale support. */
  setup() {
    useLocale()
  }

  /**
   * Renders the select.
   * @returns {ReactNode} Invoice-group select.
   */
  render() {
    const {
      defaultValue,
      defaultValues,
      label,
      multiple,
      name,
      onChangeValue,
      placeholder,
      testID,
      values
    } = this.p
    return (
      <ModelSelect
        defaultValue={defaultValue}
        defaultValues={defaultValues}
        label={label}
        multiple={multiple}
        name={name}
        onChangeValue={onChangeValue}
        options={this.tt.loadOptions}
        placeholder={placeholder || _("Search invoice groups...")}
        testID={testID}
        values={values}
      />
    )
  }

  /**
   * Loads invoice-group options for HayaSelect.
   * @param {{page?: number, searchValue?: string, values?: (string | number)[] | string | number}} args Loader args.
   * @returns {Promise<{options: {text: string, value: string}[], page: number, pageSize: number, totalCount: number}>} Paginated options.
   */
  loadOptions = async ({page = 1, searchValue, values} = {}) => {
    const selectedValues = normalizedSelectValues(values)
    if (selectedValues.length > 0) {
      const invoiceGroups = await InvoiceGroup
        .where({id: selectedValues})
        .select(["id", "name"])
        .order([["name", "asc"], ["id", "asc"]])
        .toArray()
      return modelSelectOptionsResult({
        options: invoiceGroups.map(invoiceGroupToOption),
        page: 1,
        totalCount: invoiceGroups.length
      })
    }

    /** @type {Record<string, unknown>} */
    const ransackParams = {}
    if (searchValue) ransackParams.nameCont = searchValue
    const baseQuery = InvoiceGroup.ransack(ransackParams)
    const [invoiceGroups, totalCount] = await Promise.all([
      baseQuery
        .clone()
        .select(["id", "name"])
        .order([["name", "asc"], ["id", "asc"]])
        .page(page)
        .perPage(MODEL_SELECT_PAGE_SIZE)
        .toArray(),
      baseQuery.clone().count()
    ])
    return modelSelectOptionsResult({
      options: invoiceGroups.map(invoiceGroupToOption),
      page,
      totalCount
    })
  }
}

/**
 * Converts an invoice group into a select option.
 * @param {InstanceType<typeof InvoiceGroup>} invoiceGroup Invoice group model.
 * @returns {{text: string, value: string}} Select option.
 */
function invoiceGroupToOption(invoiceGroup) {
  return {
    text: invoiceGroup.name() || String(invoiceGroup.id()),
    value: String(invoiceGroup.id())
  }
}

/** @type {NamedExoticComponent<InvoiceGroupsSelectProps>} */
const InvoiceGroupsSelectComponent = memo(shapeComponent(InvoiceGroupsSelect))

export default InvoiceGroupsSelectComponent
